import { format } from 'date-fns'
import { TOOLTIP } from '../../../../constants/tooltips'
import { InputWidth } from '../../../../types/aoraki/input-width-types'
import { FormFieldsEdit } from '../../../../types/form-types'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import useUser from '../../../hooks/use-user'
import { DISPLAY_DATE_FORMAT, INTERNAL_DATE_FORMAT } from '../../../services/constants'
import { isDriller } from '../../../services/user-utils'
import { parseDate } from '../../aoraki/generic-search-common/src'
import LookupField from '../../wells/lookup-field'
import { DateField, FormField, Heading, ReadOnlyField, TextInput } from '@msaf/core-react'
import { formatDate } from '@msaf/core-common'

interface WellHeadSecurityDetailsProps {
  isLoading: boolean
  fields: Partial<FormFieldsEdit>
  validationErrors: ValidationErrors
  setValue: SetValue<FormFieldsEdit>
}

export default function EditWellHeadSecurityQuality({
  isLoading,
  fields,
  setValue,
  validationErrors,
}: WellHeadSecurityDetailsProps) {
  const { user } = useUser()
  return (
    <>
      <Heading isSkeleton={isLoading} level={3}>
        Overall quality
      </Heading>

      {user && isDriller(user) ? (
        <FormField
          validationMessages={validationErrors?.inspectedBy}
          isSkeleton={isLoading}
          labelId='inspectedByLabel'
          label='Inspected by'
        >
          <LookupField
            isDisabled
            isSkeleton={isLoading}
            labelledBy={'inspectedByLabel'}
            fieldId='drillingCompany'
            value={`${user?.companyId ?? ''}`}
            setValue={({ value }) => setValue('inspectedBy', value)}
            isInvalid={!!validationErrors.inspectedBy}
          />
        </FormField>
      ) : (
        <FormField
          validationMessages={validationErrors?.inspectedBy}
          isSkeleton={isLoading}
          htmlFor='inspectedBy'
          label='Inspected by'
          toolTipMessage={TOOLTIP.inspectedBy}
        >
          <TextInput
            id={'inspectedBy'}
            isSkeleton={isLoading}
            value={`${fields?.inspectedBy ?? ''}`}
            isInvalid={!!validationErrors.inspectedBy}
            setValue={(val: string) => setValue('inspectedBy', val)}
          />
        </FormField>
      )}

      {user && isDriller(user) ? (
        <FormField isSkeleton={isLoading} labelId='inspectionDate' label='Inspection date'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='inspectionDate'
            value={formatDate(fields?.drillingDate, DISPLAY_DATE_FORMAT) ?? ''}
          />
        </FormField>
      ) : (
        <FormField
          toolTipMessage={TOOLTIP.inspectionDate}
          isSkeleton={isLoading}
          label='Inspection Date'
          htmlFor='inspectionDate'
        >
          <DateField
            isSkeleton={isLoading}
            isDisabled={isLoading}
            fieldSize={InputWidth.large}
            dateFormat={DISPLAY_DATE_FORMAT}
            selectedDate={parseDate(fields.inspectionDate)}
            setSelectedDate={(date: Date) => setValue('inspectionDate', date ? format(date, INTERNAL_DATE_FORMAT) : '')}
            isInvalid={!!validationErrors.inspectionDate}
          />
        </FormField>
      )}

      <FormField
        validationMessages={validationErrors?.overallSecurityRating}
        isSkeleton={isLoading}
        labelId='overallSecurityRatingLabel'
        label='Overall rating'
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'overallSecurityRatingLabel'}
          fieldId='overallSecurityRating'
          value={`${fields.overallSecurityRating ?? ''}`}
          setValue={({ value }) => setValue('overallSecurityRating', value)}
          isInvalid={!!validationErrors.overallSecurityRating}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.overallSecurityComment}
        isSkeleton={isLoading}
        htmlFor='overallSecurityComment'
        label='Comment'
      >
        <TextInput
          id={'overallSecurityComment'}
          isSkeleton={isLoading}
          value={`${fields?.overallSecurityComment ?? ''}`}
          isInvalid={!!validationErrors.overallSecurityComment}
          setValue={(val: string) => setValue('overallSecurityComment', val)}
        />
      </FormField>
    </>
  )
}
